import React, { useEffect, useRef, useState } from 'react';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';

/** Empty string must not become 0 via Number(""). */
const parseRangeNumber = (value) => {
    if (value === "" || value === null || value === undefined) {
        return null;
    }
    const num = typeof value === "number" ? value : Number(String(value).trim());
    return Number.isFinite(num) ? num : null;
};

const resolveUpper = (currentValue, min, max) => {
    const parsed = parseRangeNumber(currentValue);
    if (parsed === null) {
        return min;
    }
    return Math.min(Math.max(parsed, min), max);
};

const getInteractiveThumb = (wrapper) => {
    if (!(wrapper instanceof HTMLElement)) {
        return null;
    }

    const upperThumb = wrapper.querySelector('[role="slider"][data-upper]:not([data-disabled])');
    if (upperThumb instanceof HTMLElement) {
        return upperThumb;
    }

    const thumbs = wrapper.querySelectorAll('[role="slider"]:not([data-disabled])');
    if (thumbs.length > 1 && thumbs[1] instanceof HTMLElement) {
        return thumbs[1];
    }

    if (thumbs[0] instanceof HTMLElement) {
        return thumbs[0];
    }

    return null;
};

export default function Range({
    startValue,
    endValue,
    uniqueId,
    uuid,
    name,
    step = 1,
    currentValue,
    onLiveChange,
    onCommit,
    onDragStateChange,
    ariaLabel,
    ariaLabelledBy,
}) {
    const min = Number(startValue?.value ?? 0);
    const max = Number(endValue?.value ?? 100);
    const safeMin = Number.isFinite(min) ? min : 0;
    const safeMax = Number.isFinite(max) ? max : 100;

    if (safeMin > 0 || safeMin < 1) {
        step = safeMin;
    }

    const initialUpper = resolveUpper(currentValue, safeMin, safeMax);
    const [value, setValue] = useState([safeMin, initialUpper]);
    const latest = useRef([safeMin, initialUpper]);
    const isDraggingRef = useRef(false);
    const isInteractingRef = useRef(false);
    const wrapperRef = useRef(null);
    const activeThumbRef = useRef(null);
    const commitTimerRef = useRef(null);
    const lastCommittedRef = useRef(initialUpper);
    const onLiveChangeRef = useRef(onLiveChange);
    const onCommitRef = useRef(onCommit);
    const onDragStateChangeRef = useRef(onDragStateChange);

    useEffect(() => {
        onLiveChangeRef.current = onLiveChange;
        onCommitRef.current = onCommit;
        onDragStateChangeRef.current = onDragStateChange;
    }, [onLiveChange, onCommit, onDragStateChange]);

    const applyUpperValue = (nextUpper, focusTarget = null) => {
        const clamped = Math.min(Math.max(nextUpper, safeMin), safeMax);
        const next = [safeMin, clamped];
        latest.current = next;
        setValue(next);
        if (typeof onLiveChangeRef.current === 'function') {
            onLiveChangeRef.current(uuid, clamped);
        }
        if (commitTimerRef.current) {
            clearTimeout(commitTimerRef.current);
            commitTimerRef.current = null;
        }
        if (clamped === lastCommittedRef.current) {
            return clamped;
        }
        lastCommittedRef.current = clamped;
        if (typeof onCommitRef.current === 'function') {
            onCommitRef.current(uuid, clamped, focusTarget);
        }
        return clamped;
    };

    useEffect(() => {
        setValue(([, currentEnd]) => {
            const safeEnd = Math.min(Math.max(currentEnd, safeMin), safeMax);
            latest.current = [safeMin, safeEnd];
            return [safeMin, safeEnd];
        });
    }, [safeMin, safeMax]);

    useEffect(() => {
        if (isInteractingRef.current || isDraggingRef.current) {
            return;
        }
        const upper = resolveUpper(currentValue, safeMin, safeMax);
        if (upper === latest.current[1]) {
            return;
        }
        const next = [safeMin, upper];
        latest.current = next;
        lastCommittedRef.current = upper;
        setValue(next);
    }, [currentValue, safeMin, safeMax]);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return undefined;
        }

        const upperThumb = getInteractiveThumb(wrapper);
        if (!(upperThumb instanceof HTMLElement)) {
            return undefined;
        }

        if (uniqueId) {
            upperThumb.id = `${uniqueId}-thumb`;
        }

        const handleThumbPointerDown = () => {
            upperThumb.focus();
            activeThumbRef.current = upperThumb;
            isInteractingRef.current = true;
        };

        const handleThumbKeyDown = (event) => {
            if (event.key === 'Home') {
                event.preventDefault();
                applyUpperValue(safeMin, upperThumb);
                return;
            }

            if (event.key === 'End') {
                event.preventDefault();
                applyUpperValue(safeMax, upperThumb);
            }
        };

        upperThumb.addEventListener('pointerdown', handleThumbPointerDown, true);
        upperThumb.addEventListener('keydown', handleThumbKeyDown);

        return () => {
            upperThumb.removeEventListener('pointerdown', handleThumbPointerDown, true);
            upperThumb.removeEventListener('keydown', handleThumbKeyDown);
        };
    }, [uniqueId, value, safeMin, safeMax, uuid]);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return;
        }

        const thumbs = wrapper.querySelectorAll('[role="slider"]');
        thumbs.forEach((thumb, index) => {
            if (!(thumb instanceof HTMLElement)) {
                return;
            }

            const labelledById = Array.isArray(ariaLabelledBy)
                ? ariaLabelledBy[index]
                : undefined;
            const labelText = Array.isArray(ariaLabel)
                ? ariaLabel[index]
                : index === 1
                    ? ariaLabel
                    : undefined;

            if (labelledById) {
                thumb.setAttribute('aria-labelledby', labelledById);
                thumb.removeAttribute('aria-label');
                return;
            }

            if (labelText) {
                thumb.setAttribute('aria-label', labelText);
                thumb.removeAttribute('aria-labelledby');
            }
        });
    }, [value, ariaLabel, ariaLabelledBy, uniqueId]);

    useEffect(() => {
        return () => {
            if (commitTimerRef.current) {
                clearTimeout(commitTimerRef.current);
            }
        };
    }, []);

    const getActiveThumb = () => {
        const focused = wrapperRef.current?.querySelector('[role="slider"]:focus');
        if (focused instanceof HTMLElement) {
            return focused;
        }
        return activeThumbRef.current instanceof HTMLElement
            ? activeThumbRef.current
            : null;
    };

    const commitValue = (focusTarget = null) => {
        if (commitTimerRef.current) {
            clearTimeout(commitTimerRef.current);
            commitTimerRef.current = null;
        }
        const next = latest.current || value;
        const nextUpper = next[1];
        if (nextUpper === lastCommittedRef.current) {
            return;
        }
        lastCommittedRef.current = nextUpper;
        if (typeof onCommit === 'function') {
            onCommit(uuid, nextUpper, focusTarget || getActiveThumb());
        }
    };

    const scheduleCommit = () => {
        if (commitTimerRef.current) {
            clearTimeout(commitTimerRef.current);
        }
        commitTimerRef.current = setTimeout(() => {
            commitTimerRef.current = null;
            if (isInteractingRef.current) {
                commitValue(getActiveThumb());
            }
        }, 500);
    };

    const handleInput = (newValue) => {
        const upper = Array.isArray(newValue) ? newValue[1] : Number(newValue);
        const clamped = Math.min(Math.max(upper, safeMin), safeMax);
        const next = [safeMin, clamped];
        latest.current = next;
        setValue(next);
        if (typeof onLiveChange === 'function') {
            onLiveChange(uuid, clamped);
        }
        if (!isDraggingRef.current) {
            scheduleCommit();
        }
    };

    const handleThumbDragStart = () => {
        isDraggingRef.current = true;
        isInteractingRef.current = true;
        if (typeof onDragStateChangeRef.current === 'function') {
            onDragStateChangeRef.current(true);
        }
        if (commitTimerRef.current) {
            clearTimeout(commitTimerRef.current);
            commitTimerRef.current = null;
        }
    };

    const handleThumbDragEnd = () => {
        isDraggingRef.current = false;
        if (typeof onDragStateChangeRef.current === 'function') {
            onDragStateChangeRef.current(false);
        }
        const upperThumb = getInteractiveThumb(wrapperRef.current);
        if (upperThumb instanceof HTMLElement) {
            upperThumb.focus();
            activeThumbRef.current = upperThumb;
        }
        commitValue(upperThumb);
    };

    const handleFocusIn = (event) => {
        isInteractingRef.current = true;
        if (
            event.target instanceof HTMLElement &&
            event.target.getAttribute('role') === 'slider'
        ) {
            activeThumbRef.current = event.target;
        }
    };

    const handleFocusOut = (event) => {
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return;
        }
        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && wrapper.contains(nextTarget)) {
            return;
        }
        isInteractingRef.current = false;
        commitValue(activeThumbRef.current);
    };

    const sliderAriaLabel = ariaLabel;

    return (
        <div
            ref={wrapperRef}
            className="range-slider-custom"
            data-roi-focus-id={`${uniqueId}-range`}
            onFocusIn={handleFocusIn}
            onFocusOut={handleFocusOut}
        >
            <RangeSlider
                id={uniqueId}
                value={value}
                min={safeMin}
                max={safeMax}
                step={step}
                onInput={handleInput}
                onThumbDragStart={handleThumbDragStart}
                onThumbDragEnd={handleThumbDragEnd}
                thumbsDisabled={[true, false]}
                ariaLabel={sliderAriaLabel}
                ariaLabelledBy={ariaLabelledBy}
            />
            {name && (
                <input type="hidden" name={name} value={value[1]} />
            )}
        </div>
    );
}
